import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useGetOnboardingWorkTemplateCatalogQuery } from "legal-entities/agreements/_api/entity-agreement-api-service";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import type { UserOnboardingWork } from "users/information/user-onboarding-work";
import {
  useCloseOnboardingMutation,
  useReplaceOnboardingProposalMutation,
} from "users/super-users/_api/super-user-api";

type OnboardingWorkResolutionProps = {
  onboardingWork: UserOnboardingWork;
  onChanged: () => void;
};

export default function OnboardingWorkResolution({ onboardingWork, onChanged }: Readonly<OnboardingWorkResolutionProps>) {
  const { data: templateData } = useGetOnboardingWorkTemplateCatalogQuery();
  const templates = templateData ?? [];
  const [replaceProposal, replaceState] = useReplaceOnboardingProposalMutation();
  const [closeOnboarding, closeState] = useCloseOnboardingMutation();
  const [templateId, setTemplateId] = useState(onboardingWork.templateId);
  const [reason, setReason] = useState("");

  const selectedTemplateId = templates.some((template) => template.id === templateId)
    ? templateId
    : templates.find((template) => template.isDefault)?.id ?? templates[0]?.id ?? onboardingWork.templateId;

  const isClosed = onboardingWork.state === "Closed";
  const isCompleted = onboardingWork.state === "Completed";
  const canReplace = onboardingWork.state === "StaffResolutionRequired" && Boolean(selectedTemplateId);
  const canClose = !isClosed && !isCompleted;

  async function handleReplace() {
    if (!selectedTemplateId) return;
    try {
      await replaceProposal({ requestId: onboardingWork.requestId, templateId: selectedTemplateId }).unwrap();
      enqueueSnackbar("Replacement proposal created", { variant: "success" });
      onChanged();
    } catch {
      enqueueSnackbar("Could not create replacement proposal", { variant: "error" });
    }
  }

  async function handleClose() {
    if (!reason.trim()) return;
    try {
      await closeOnboarding({ requestId: onboardingWork.requestId, reason: reason.trim() }).unwrap();
      enqueueSnackbar("Onboarding closed", { variant: "success" });
      setReason("");
      onChanged();
    } catch {
      enqueueSnackbar("Could not close onboarding", { variant: "error" });
    }
  }

  return (
    <Stack spacing={0.75} sx={{ minWidth: 280, py: 0.5 }}>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
        <Chip
          size="small"
          color={isClosed ? "default" : isCompleted ? "success" : onboardingWork.state === "StaffResolutionRequired" ? "warning" : "info"}
          label={onboardingWork.state}
          icon={isCompleted ? <CheckCircleOutlinedIcon /> : isClosed ? <CloseIcon /> : undefined}
        />
        <Typography variant="caption">Template {onboardingWork.templateId} v{onboardingWork.templateVersion}</Typography>
      </Stack>
      <Typography variant="caption">Current proposal: {onboardingWork.currentProposalId ?? "—"}</Typography>
      <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
        Proposal history ({onboardingWork.relatedProposalIds.length}): {onboardingWork.relatedProposalIds.join(", ") || "—"}
      </Typography>
      {onboardingWork.state === "Closed" && (
        <Typography variant="caption" color="text.secondary">
          Closure reason: {onboardingWork.closureReason ?? "Not recorded"}
        </Typography>
      )}
      {canReplace && (
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 165 }}>
            <InputLabel id={`replacement-template-${onboardingWork.requestId}`}>Replacement template</InputLabel>
            <Select
              labelId={`replacement-template-${onboardingWork.requestId}`}
              value={selectedTemplateId}
              label="Replacement template"
              onChange={(event) => setTemplateId(event.target.value)}
            >
              {templates.filter((template) => template.workflowVersion >= 2).map((template) => (
                <MenuItem key={`${template.id}:${template.version}`} value={template.id}>
                  {template.displayName} ({template.version})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ReplayIcon />}
            disabled={!canReplace || replaceState.isLoading}
            onClick={() => void handleReplace()}
          >
            Replace
          </Button>
        </Stack>
      )}
      {canClose && (
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
          <TextField
            size="small"
            label="Closure reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            slotProps={{ input: { "aria-label": `Closure reason for ${onboardingWork.requestId}` } }}
          />
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<CloseIcon />}
            disabled={!reason.trim() || closeState.isLoading}
            onClick={() => void handleClose()}
          >
            Close
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
